// UI-3: Modal, ConfirmDialog, SidePanel and Sheet. design-brief.md section 3 ("Modals", "Side
// panels"); mobile-responsive-addendum.md 6. All stand on Overlay.jsx: a portal, the open
// registry, scroll lock, focus trap with focus return, Escape, and the iOS keyboard fit.
import React, { useId, useRef, useState, useEffect } from 'react';
import { v } from './tokens';
import { Button } from './Button';
import { Layer, useOverlay, CloseButton } from './Overlay';
import './v2.css';

const cx = (...parts) => parts.filter(Boolean).join(' ');

// The header band: a pastel (or the highlight, for Add task) with the title and ✕.
function Band({ id, title, color, onClose, closeLabel }) {
  return (
    <div className="ui-overlay__band" style={{ '--ui-bg': v(color) }}>
      <h2 id={id} className="ui-overlay__title">{title}</h2>
      {onClose && <CloseButton onClick={onClose} label={closeLabel} />}
    </div>
  );
}

// Modal: header band, a body that scrolls, a footer that stays (a flex column: no fixed footer,
// so the iOS keyboard cannot push it off screen). Desktop: centred, min(width, 100vw - 32px).
// Phone: a full screen sheet; variant="confirm" (one sentence and two buttons) is a bottom sheet.
// footer: the buttons (Cancel first, the action last); on a phone they split 50/50.
export function Modal({
  open, onClose, title, color = 'pastel-blue', footer, children, variant = 'default', width = 560,
  initialFocus, closeOnBackdrop = true, closeLabel = 'Close', describedBy,
}) {
  const ref = useRef(null);
  const titleId = useId();
  useOverlay(ref, open, { onClose, initialFocus });
  if (!open) return null;
  return (
    <Layer>
      <div className="ui-backdrop" aria-hidden="true" onClick={closeOnBackdrop ? onClose : undefined} />
      <div ref={ref} role={variant === 'confirm' ? 'alertdialog' : 'dialog'} aria-modal="true" aria-labelledby={titleId}
        aria-describedby={describedBy} tabIndex={-1}
        className={cx('ui-modal', variant === 'confirm' && 'ui-modal--confirm')} style={{ '--ui-modal-width': `${width}px` }}>
        <Band id={titleId} title={title} color={color} onClose={onClose} closeLabel={closeLabel} />
        <div className="ui-overlay__body">{children}</div>
        {footer && <div className="ui-overlay__footer">{footer}</div>}
      </div>
    </Layer>
  );
}

// The v2 ConfirmDialog (replaces ConfirmDeleteModal on Business pages; Personal keeps the old one
// for now). Same props as the old one so a page swaps by changing the import: itemType, itemName,
// warningText. typeToConfirm asks for the name to be typed. Decision 53: off by default; on only
// for actions with no Undo (deleting a business account, transferring ownership, permanently
// removing a team member). The Delete button is the coral `delete` variant (decision 51). onConfirm may return a promise: the button
// waits, and a rejection shows the real message inside the dialog. defaultCancel puts the focus on
// Cancel, so Enter cancels: for a delete that cannot be undone (Carla, 2026-10-21, IMAP no Trash).
export function ConfirmDialog({
  open, onClose, onConfirm, title, message, confirmLabel, cancelLabel = 'Cancel', destructive = true,
  itemType, itemName, warningText, typeToConfirm = false, defaultCancel = false, children,
}) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const msgId = useId();
  useEffect(() => { if (open) { setTyped(''); setError(null); setBusy(false); } }, [open]);
  const heading = title || (itemType ? `Delete ${itemType}?` : 'Are you sure?');
  const action = confirmLabel || (destructive ? 'Delete' : 'Confirm');
  const blocked = typeToConfirm && typed !== itemName;
  const confirm = async () => {
    if (blocked || busy) return;
    setBusy(true); setError(null);
    try { await onConfirm?.(); onClose?.(); } catch (e) { setError(e?.message || 'That did not work. Please try again.'); setBusy(false); }
  };
  return (
    <Modal open={open} onClose={busy ? undefined : onClose} title={heading} color={destructive ? 'pastel-coral' : 'pastel-blue'}
      variant="confirm" width={440} describedBy={msgId}
      footer={<>
        <Button variant="tertiary" onClick={onClose} disabled={busy} data-autofocus={defaultCancel ? '' : undefined}>{cancelLabel}</Button>
        <Button variant={destructive ? 'delete' : 'confirm'} onClick={confirm} disabled={blocked || busy} aria-busy={busy || undefined} data-autofocus={typeToConfirm || defaultCancel ? undefined : ''}>
          {busy ? 'Working…' : action}
        </Button>
      </>}>
      <p id={msgId} className="ui-confirm__text">{message || warningText}{itemName && !message && !warningText ? <> <strong>{itemName}</strong></> : null}</p>
      {children /* an option that belongs to this decision, e.g. an unticked "Email the client again" */}
      {typeToConfirm && (
        <label className="ui-field">
          <span className="ui-field__label">Type <strong>{itemName}</strong> to confirm</span>
          <input className="ui-input" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
        </label>
      )}
      {error && <p className="ui-field__error" role="alert">{error}</p>}
    </Modal>
  );
}

// SidePanel: a detail drawer from the right, min(420px, 100vw), header band in the category
// colour, the body scrolls, actions pinned at the bottom. Phone: full screen.
export function SidePanel({ open, onClose, title, color = 'pastel-mint', actions, children, closeLabel = 'Close' }) {
  const ref = useRef(null);
  const titleId = useId();
  useOverlay(ref, open, { onClose });
  if (!open) return null;
  return (
    <Layer>
      <div className="ui-backdrop ui-backdrop--panel" aria-hidden="true" onClick={onClose} />
      <aside ref={ref} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} className="ui-panel">
        <Band id={titleId} title={title} color={color} onClose={onClose} closeLabel={closeLabel} />
        <div className="ui-overlay__body">{children}</div>
        {actions && <div className="ui-overlay__footer">{actions}</div>}
      </aside>
    </Layer>
  );
}

// Sheet: the bottom sheet (drag handle, 80dvh at most, 48px rows) on a phone, and a small centred
// dialog on tablet and desktop. The one sheet every phone menu, dropdown, popover and Move to
// picker uses. role: 'dialog' by default; a menu passes role="menu" content inside.
export function Sheet({ open, onClose, title, children, footer, labelledBy, color }) {
  const ref = useRef(null);
  const titleId = useId();
  useOverlay(ref, open, { onClose });
  if (!open) return null;
  return (
    <Layer>
      <div className="ui-backdrop ui-backdrop--sheet" aria-hidden="true" onClick={onClose} />
      <div ref={ref} role="dialog" aria-modal="true" aria-labelledby={labelledBy || (title ? titleId : undefined)} aria-label={title || labelledBy ? undefined : 'Options'}
        tabIndex={-1} className="ui-sheet" style={color ? { '--ui-bg': v(color) } : undefined}>
        <span className="ui-sheet__handle" aria-hidden="true" />
        {title && <h2 id={titleId} className="ui-sheet__title">{title}</h2>}
        <div className="ui-sheet__body">{children}</div>
        {footer && <div className="ui-overlay__footer">{footer}</div>}
      </div>
    </Layer>
  );
}
